import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@anytag/shared-utils';

// https://www.figma.com/file/VVKblM8mDjRpkaSwyPw0ky/2024-Components?node-id=6209%3A29991&mode=dev
const buttonVariants = cva(
  cn(
    'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive inline-flex flex-none shrink-0 cursor-pointer items-center justify-center gap-2 rounded-[3px] px-6 py-0 text-sm font-semibold whitespace-nowrap transition-all outline-none [&_svg]:pointer-events-none',
    // Disabled styles
    'disabled:text-black-50 disabled:pointer-events-none disabled:border-1 disabled:border-black-30 disabled:bg-black-10',
    // aria-disabled styles
    'aria-disabled:text-black-50 aria-disabled:pointer-events-none aria-disabled:border-1 aria-disabled:border-black-30 aria-disabled:bg-black-10'
  ),
  {
    variants: {
      variant: {
        default: cn('border-1 border-black-30 bg-white text-black-60 hover:border-black-40 hover:text-black-90'),
        blue: cn('bg-blue-70 text-white hover:bg-blue-90 hover:text-white'),
        black: cn('bg-black-90 text-white hover:bg-black-60 hover:text-white'),
        red: cn('bg-red-70 text-white hover:bg-red-90 hover:text-white'),
        coralRed: cn('bg-red-80 text-white hover:bg-red-50 hover:text-white'),
        purple: cn('bg-purple-70 text-white hover:bg-purple-60 hover:text-white active:bg-purple-70'),
        orange: cn('bg-orange-70 text-white hover:bg-orange-90 hover:text-white'),
        green: cn('bg-dark-green-70 text-white hover:bg-dark-green-60 active:bg-dark-green-90'),
        // We don't have asset for outline button yet
        'blue-outline': cn('border-1 border-blue-70 bg-transparent text-blue-70 hover:opacity-80'),
        'blue-outline-filled': cn('border-1 border-blue-70 bg-[#EDF6FF] text-blue-70 hover:opacity-80'),
        'red-outline-filled': cn('border-1 border-red-70 bg-red-10 text-red-70 hover:opacity-80'),
        'any-ai-gradient': cn(
          'gradient-border-button bg-white text-black-60',
          'hover:text-black-90',
          'disabled:pointer-events-auto disabled:opacity-60'
        )
      },
      size: { default: cn('h-8'), sm: cn('h-6'), lg: cn('h-10') }
    },
    defaultVariants: { variant: 'default', size: 'default' }
  }
);

function Button({
  size,
  variant,
  className,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} type="button" {...props} />
  );
}

export { Button, buttonVariants };
